import * as Sentry from "@sentry/react";

interface SentryRuntimeConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: string;
}

declare global {
  interface Window {
    __SENTRY_CONFIG__?: SentryRuntimeConfig;
  }
}

function optionalValue(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

function parseTracesSampleRate(value: string | undefined): number {
  const normalized = value?.trim();
  if (!normalized) return 0;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    throw new Error(
      "VITE_SENTRY_TRACES_SAMPLE_RATE must be a finite number between 0 and 1",
    );
  }
  return parsed;
}

/** Initialize browser error monitoring from container-provided runtime config. */
export function initSentry(service: string): boolean {
  const config = window.__SENTRY_CONFIG__;
  const dsn = optionalValue(config?.dsn);
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: optionalValue(config?.environment),
    release: optionalValue(config?.release),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: parseTracesSampleRate(config?.tracesSampleRate),
    tracePropagationTargets: [/^\/api\//],
    sendDefaultPii: false,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    initialScope: { tags: { service } },
  });
  return true;
}
